/**
 * Thin wrappers around the orval-generated batch pipeline hooks (TRA-863).
 *
 * A team sees only the pipelines staff granted it, so `usePipelines`
 * answering [] is the normal case and the Models page renders nothing for
 * it. Lists and details poll while a batch is still owned by the dispatch
 * machinery (queued, dispatched, running) so counts move without a
 * reload, the same rule the benchmark cards use. Same unwrap pattern as
 * useBenchmarks.ts (the orval FETCH client returns {data, status, headers}).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import type { ApiRequestError } from '@/api/client'
import type {
  cancelBatchApiBatchesBatchIdCancelPostResponse,
  createBatchApiBatchesPostResponse,
} from '@/api/generated/batches/batches'
import {
  createBatchApiBatchesPost,
  getGetBatchApiBatchesBatchIdGetQueryKey,
  getListBatchesApiBatchesGetQueryKey,
  getListBatchItemsApiBatchesBatchIdItemsGetQueryKey,
  getListPipelinesApiPipelinesGetQueryKey,
  useCancelBatchApiBatchesBatchIdCancelPost,
  useGetBatchApiBatchesBatchIdGet,
  useListBatchesApiBatchesGet,
  useListBatchItemsApiBatchesBatchIdItemsGet,
  useListPipelinesApiPipelinesGet,
} from '@/api/generated/batches/batches'
import type {
  BatchCreate,
  BatchItemsResponse,
  BatchListResponse,
  BatchResponse,
  ListBatchItemsApiBatchesBatchIdItemsGetParams,
  PipelinesResponse,
} from '@/api/generated/model'

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

const ACTIVE_REFETCH_MS = 15_000

/**
 * Still moving: no finished_at yet. A status set would be wrong here.
 * Cancelling a running batch flips its status at once while the worker
 * keeps finishing the in-flight files and stamps finished_at only when it
 * is done, and a requeued batch clears finished_at and moves again.
 */
export function isBatchActive(batch: Pick<BatchResponse, 'finished_at'>): boolean {
  return batch.finished_at === null
}

/**
 * The granted pipelines with each one's summary (newest batch, count still
 * moving). Polls while any newest batch is still moving so the Models band
 * keeps up, and the create and cancel mutations below invalidate it.
 */
export function usePipelines(): UnwrappedQuery<PipelinesResponse> {
  const query = useListPipelinesApiPipelinesGet({
    query: {
      refetchInterval: (current) => {
        const body = current.state.data
        const pipelines = body?.status === 200 ? body.data.pipelines : []
        return pipelines.some((p) => p.latest_batch !== null && isBatchActive(p.latest_batch))
          ? ACTIVE_REFETCH_MS
          : false
      },
    },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/** One page of the team's batches, newest first, optionally one pipeline's. */
export function useBatches(params: {
  pipeline?: string
  offset: number
  limit: number
}): UnwrappedQuery<BatchListResponse> {
  const query = useListBatchesApiBatchesGet(
    { pipeline: params.pipeline, offset: params.offset, limit: params.limit },
    {
      query: {
        refetchInterval: (current) => {
          const body = current.state.data
          const batches = body?.status === 200 ? body.data.batches : []
          return batches.some(isBatchActive) ? ACTIVE_REFETCH_MS : false
        },
      },
    },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useBatch(batchId: string): UnwrappedQuery<BatchResponse> {
  const query = useGetBatchApiBatchesBatchIdGet(batchId, {
    query: {
      refetchInterval: (current) => {
        const body = current.state.data
        return body?.status === 200 && isBatchActive(body.data) ? ACTIVE_REFETCH_MS : false
      },
    },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * One page of a batch's items. Polls at the batch's cadence while the
 * caller says the batch is active: the items query cannot tell on its own.
 */
export function useBatchItems(
  batchId: string,
  params: ListBatchItemsApiBatchesBatchIdItemsGetParams,
  opts: { active: boolean },
): UnwrappedQuery<BatchItemsResponse> {
  const query = useListBatchItemsApiBatchesBatchIdItemsGet(batchId, params, {
    query: { refetchInterval: opts.active ? ACTIVE_REFETCH_MS : false },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export interface CreateBatchVariables {
  data: BatchCreate
  /** See components/pipelines/idempotency.ts: stable for an unchanged form, fresh after an edit. */
  idempotencyKey: string
}

/**
 * Submit a batch. The key rides per call because it is derived from the
 * payload, so a retry of the same form returns the batch it already
 * created while an edited form creates a new one (the API's
 * Idempotency-Key contract). The generated hook can only fix headers at
 * construction, so this wraps the generated client function instead.
 */
export function useCreateBatch(): UseMutationResult<
  createBatchApiBatchesPostResponse,
  ApiRequestError,
  CreateBatchVariables
> {
  const queryClient = useQueryClient()
  return useMutation<createBatchApiBatchesPostResponse, ApiRequestError, CreateBatchVariables>({
    mutationFn: ({ data, idempotencyKey }) =>
      createBatchApiBatchesPost(data, { headers: { 'Idempotency-Key': idempotencyKey } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: getListBatchesApiBatchesGetQueryKey() })
      void queryClient.invalidateQueries({ queryKey: getListPipelinesApiPipelinesGetQueryKey() })
    },
  })
}

export function useCancelBatch(): UseMutationResult<
  cancelBatchApiBatchesBatchIdCancelPostResponse,
  ApiRequestError,
  { batchId: string }
> {
  const queryClient = useQueryClient()
  return useCancelBatchApiBatchesBatchIdCancelPost({
    mutation: {
      onSuccess: (_result, { batchId }) => {
        void queryClient.invalidateQueries({
          queryKey: getGetBatchApiBatchesBatchIdGetQueryKey(batchId),
        })
        void queryClient.invalidateQueries({
          queryKey: getListBatchItemsApiBatchesBatchIdItemsGetQueryKey(batchId),
        })
        void queryClient.invalidateQueries({ queryKey: getListBatchesApiBatchesGetQueryKey() })
        void queryClient.invalidateQueries({ queryKey: getListPipelinesApiPipelinesGetQueryKey() })
      },
    },
  })
}
